import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute as ContextRoute } from '@angular/router';
import { Optional as ContextOptional, ElementRef as ContextElement } from '@angular/core';
import { Subscription as ContextSubscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataFetch } from '../../../models/data-source';
import { MapFeatureColors, MapObjectPalette } from '../../../map/core/map-object-colors';
import { TerritoryFallbackColor, territoryPaletteColor } from '../../../map/features/territories/territory-colors';
import { Component, HostListener, OnDestroy, OnInit, ViewChild, HostBinding } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, from, of, throwError, timer } from 'rxjs';
import {
  catchError, concatMap, map, switchMap, takeUntil, toArray,
} from 'rxjs/operators';
import * as L from 'leaflet';
import { Router } from '@angular/router';
import { DataTableColumn, DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import {
  DataFilterField, DataFilterOption, DataFilterValues,
} from '../../../shared/ui/data-filter/data-filter.component';
import {
  DataFilterReader, matchesFilters,
} from '../../../shared/ui/data-filter/data-filter.match';
import { toRuleParams } from '../../../shared/ui/data-filter/data-filter.rules';
import { EditSession } from '../../../shared/edit-session/edit-session';
import { EditSessionRegistry } from '../../../shared/edit-session/edit-session-registry.service';
import { AuthService } from '../../../services/auth.service';
import { MapFeatureManagerService } from '../../../map/core/map-feature-manager.service';
import { MapInteractionController } from '../../../map/components/map/map-interaction-controller';
import {
  Territory, TerritoryFeature, TerritoryWithMetrics,
} from '../../../map/features/territories/territory-feature';
import {
  LEAD_LIST_COLUMNS, leadActionOverdue, leadSourceLabel,
} from '../board/lead-list-table';
import { businessTypeLabels } from '../board/business-types';
import { stageColor } from '../board/stage-colors';
import { leadRuleFilterFields } from '../board/lead-filters';
import type { LeadStage, WorkItem } from '../board/leads-board.component';
import {
  TerritoryPlanChoice, TerritoryPlanDialogComponent, TerritoryPlanFacts,
} from './territory-plan-dialog.component';
import { TerritoryDrawController } from './territory-draw-controller';
import { ringAreaSqMeters, ringsOf, ringsToGeoJson } from '../../../map/features/territories/boundary-snapping';
import { validateBoundaryGeoJson, validateRings } from './boundary-validation';
import {
  DraftKey, DraftOp, DraftState, keyOf, reduceDraft, toDraftInput,
} from './territory-draft';
import {
  SheetAction, SheetScope, territorySheetsCsv, territorySheetsFilename, territorySheetsHtml,
} from './territory-export';
import { triggerBlobDownload } from '../../../shared/download.util';
import { todayLocalDateString } from '../../../shared/date.util';

// Re-exported so the roster template, the specs and anything already importing
// them from here keep working; the shapes now live with the map layer that
// renders them.
export { Territory, TerritoryWithMetrics };
/** One door as the table shows it. Mirrors TerritoryStopRowDto. */
export interface TerritoryStopRow {
  leadId: number;
  name: string | null;
  addressLine1: string | null;
  city: string | null;
  lat: number;
  lng: number;
  territoryId: number | null;
  territoryName: string | null;
  repUserId: number | null;
  repName: string | null;
}

export interface TerritoryStopList {
  items: TerritoryStopRow[];
  total: number;
  page: number;
  pageSize: number;
}

/** One page of `/api/lead-work-items`, which is what the Leads list reads. */
interface LeadWorkItemList {
  items: WorkItem[];
  total: number;
}

/** The territory details that can be edited in place. */
/**
 * The territory details that can be edited in place.
 *
 * Code and Branch were here and are gone (2026-09-03). Nothing read them:
 * no query filtered, sorted or grouped by either, and no screen but this one
 * showed them - they were two free-text boxes whose only effect was to be
 * carried through every create, update and snapshot. A field a person can
 * fill in and never see again is worse than no field, because it invites
 * work that goes nowhere.
 */
/**
 * The one detail still edited in place.
 *
 * Status and rep used to be here too, as text that turned into a control when
 * clicked. They are closed dropdowns now - always a control, whether or not
 * anybody is changing one - so nothing has to remember which of them is open.
 * A name is different: it is a line of prose, it is set once and changed close
 * to never, and a box around it at rest is a form pretending to be a heading.
 */
export type DetailField = 'name';


/**
 * What a merge did, and everything needed to take it back.
 *
 * `survivorWas` is the survivor's own outline before the union, and
 * `absorbed` is what each folded-in territory WAS - they are deleted rows by
 * the time this arrives, so this object is the only record of them. It goes
 * back to the server verbatim to undo, which is why nothing here is reshaped
 * on the way in.
 */
export interface TerritoryMergeResult {
  survivor: Territory;
  survivorWas: Territory;
  absorbed: { was: Territory }[];
  leadsMoved: number;
}

export interface AssignableRep { userId: number; userName: string | null; displayName: string | null; }

/** One automatic repair, in the terms an admin needs to judge it. */
export interface OverlapRepair {
  territoryId: number | null; territoryTempId: number | null; name: string;
  removedSqKm: number; remainingSqKm: number;
  againstEditSequence: number; againstTerritoryId: number | null;
  againstTerritoryTempId: number | null; againstName: string | null; stopsMoved: number;
}

/** One door changing hands, named by territory rather than by rep. */
export interface StopMove {
  leadId: number; name: string | null;
  fromTerritoryId: number | null; fromTerritoryName: string | null;
  toTerritoryId: number | null; toTerritoryName: string | null;
  reason: string;
}

export interface ResolvedTerritory {
  id: number | null; tempId: number | null; name: string; status: string;
  boundaryGeoJson: string; assignedUserId: number | null; color: string | null;
  stopCount: number; areaSqKm: number; boundaryCleared: boolean;
}

/** What the draft WOULD become. Preview and commit resolve identically, so what
 *  the admin approved is what lands. */
export interface DraftResolution {
  territories: ResolvedTerritory[];
  repairs: OverlapRepair[];
  stopMoves: StopMove[];
  unassignedStopCount: number;
  hasBlockingError: boolean;
  errors: string[];
}

export interface DraftCommit {
  territories: TerritoryWithMetrics[];
  resolution: DraftResolution;
  stopsReassigned: number;
}

export interface TerritoryConflict {
  territoryId: number; name: string; expectedVersion: number; actualVersion: number;
}

/**
 * Sales territory management: draw a boundary, give it to a rep, see what the
 * patch is worth.
 *
 * A control surface. Boundaries render on the persistent workspace map through
 * TerritoryFeature, and drawing runs through TerritoryDrawController, which
 * borrows the shared map's gestures and gives them back. This component keeps
 * the roster, metrics, form, assignment, AI planning, overlaps and errors — and
 * holds no L.Map and no tile layer of its own.
 */
@Component({
  selector: 'app-territories',
  templateUrl: './territories.component.html',
  styleUrls: ['./territories.component.css'],
})
export class TerritoriesComponent implements OnInit, OnDestroy {
  @ViewChild('compactList') compactList?: DataListComponent<WorkItem>;
  prospectSearch = '';

  readonly statuses = ['Draft', 'Active', 'Retired'];
  readonly mapColors = MapFeatureColors;
  readonly displayColor = territoryPaletteColor;

  /**
   * What a new territory is coloured, so that two drawn in a row are told
   * apart on the map without anybody choosing.
   *
   * There is no colour picker any more. A territory still HAS a colour - the
   * dot beside its name, the shade of its shape - but choosing it was a
   * control on the detail panel for a decision made about once in a
   * territory's life, sitting beside the two that actually get changed.
   */
  readonly palette = MapObjectPalette;

  rows: TerritoryWithMetrics[] = [];
  loading = false;
  loadError: string | null = null;
  saveError: string | null = null;
  saving = false;

  selectedId: number | null = null;

  /**
   * The territories ticked for merging, IN THE ORDER THEY WERE TICKED.
   *
   * Order is the whole answer to "which one survives": the first keeps its
   * name, code, colour, rep, branch and depot, and the rest give up their
   * ground and their leads. An unordered set would leave that to the server
   * and make the outcome unguessable from the screen, so the bar names the
   * survivor rather than asking anybody to remember what they clicked first.
   */
  mergePicks: number[] = [];
  merging = false;
  /**
   * The last merge, kept only so it can be taken back.
   *
   * It is the ONLY account of what was merged - the absorbed territories are
   * deleted rows by now - so undo is offered for as long as this is held and
   * no longer: another merge replaces it, undoing clears it, and leaving the
   * page ends it. The bar says so rather than leaving somebody to find out
   * by coming back tomorrow.
   */
  lastMerge: TerritoryMergeResult | null = null;
  mergeNote: string | null = null;
  reps: AssignableRep[] = [];
  /** Agent planner state. planCount null = one patch per rep. */
  planning = false;
  planNote: string | null = null;
  planCount: number | null = null;
  /** ~30s of polling at 2s: long enough for a full book, short enough that a
   *  stuck job says so instead of spinning forever. */
  static readonly PlanPollMs = 2000;
  static readonly PlanPollAttempts = 15;
  /** Roster filter: only the patches I own. */
  mineOnly = false;
  private myUserId: number | null = null;

  // ── The roster's filter bar ────────────────────────────────────────────
  //
  // The same shared bar the Leads list carries, answered here rather than by
  // the database: this roster is a company's whole list of territories,
  // fetched once, and it is already in the browser. matchesFilters is what
  // makes that possible without a second dialect of the same grammar.

  filterFields: DataFilterField[] = [];
  filterValues: DataFilterValues = {};

  /**
   * How each filter column is read out of a roster row.
   *
   * Every field declared above needs one here, or its rule would be skipped
   * and quietly match everything. The two lists are held together by a test
   * rather than by care.
   */
  readonly filterReaders: Record<string, DataFilterReader<TerritoryWithMetrics>> = {
    name: row => row.territory.name,
    status: row => row.territory.status,
    // By id, which is what the dropdown sends. A patch with nobody on it
    // answers the "Unassigned" row and no other.
    rep: row => row.territory.assignedUserId ?? 'unassigned',
    leads: row => row.leads,
    // Not counted yet - a sketched patch carries no count - is not zero
    // customers. Left missing, it matches no rule: the shared matcher's answer.
    customers: row => row.customers,
    pipeline: row => row.openPipelineValue,
  };

  /**
   * Rebuilt when the roster arrives rather than computed in a getter: the
   * template reads this on every change-detection pass, and a fresh array each
   * time would re-enter the filter bar's ngOnChanges for no reason.
   *
   * Unpinned, so the head stays as short as it was until somebody opens
   * Filters - this page's chrome is already carrying a view switch, a Mine
   * toggle and an edit-session bar over a map.
   */
  private buildFilterFields(): void {
    const none: readonly DataFilterOption[] = [];
    this.filterFields = [
      { id: 'name', label: 'Name', kind: 'text', pinned: false, options: none },
      {
        id: 'status', label: 'Status', pinned: false, allLabel: 'Any status',
        options: this.statuses.map(status => ({ value: status, label: status })),
      },
      {
        id: 'rep', label: 'Rep', pinned: false, allLabel: 'Any rep',
        // "Unassigned" is a value here, not the absence of one: a patch with
        // nobody on it is the thing somebody most often comes to this list to
        // find, and it cannot be asked for by leaving the filter off.
        options: [
          { value: 'unassigned', label: 'Unassigned' },
          ...this.reps.map(rep => ({
            value: rep.userId, label: this.repLabel(rep.userId),
          })),
        ],
      },
      { id: 'leads', label: 'Leads', kind: 'number', pinned: false, options: none },
      { id: 'customers', label: 'Customers', kind: 'number', pinned: false, options: none },
      {
        id: 'pipeline', label: 'Open pipeline', kind: 'number', pinned: false,
        options: none,
      },
    ];
  }

  /** A rule was added, changed or removed on the filter bar. */
  applyFilters(values: DataFilterValues): void {
    this.filterValues = values;
  }

  /** Whether any rule is narrowing the roster, for the empty state to explain. */
  get anyFilter(): boolean {
    return Object.values(this.filterValues).some(held => held !== null && held !== '');
  }

  /**
   * The way out of a roster a rule emptied.
   *
   * Also turns Mine off. Both narrow this list, and somebody looking at
   * "nothing matches" has no way to tell which of the two did it - offering to
   * clear only half of the answer would leave the list empty and the button
   * apparently broken.
   */
  clearFilters(): void {
    this.filterValues = Object.fromEntries(
      this.filterFields.map(field => [field.id, null]));
    this.mineOnly = false;
  }

  /** Set when a draw is meant to replace an existing boundary. */
  redrawTargetId: number | null = null;

  /**
   * Why the selected patch is not wearing handles, when it is not.
   *
   * Two different situations, and they used to read as one line of red text.
   * 'missing' is a territory nobody has drawn yet - normal, expected of a patch
   * created by name, and fixed by drawing one. 'unusable' is a territory that HAS
   * a boundary which this page cannot put corners on: an import, a geometry type
   * the editor does not handle, something truncated. The first is a blank; the
   * second is damage, and saying "draw one" to somebody who can see a shape on
   * the map would read as the page being wrong about its own data.
   *
   * Null while a boundary is open for reshaping, and while nothing is selected.
   */
  boundaryIssue: 'missing' | 'unusable' | null = null;

  form = { name: '', status: 'Draft', assignedUserId: null as number | null,
           color: TerritoryFallbackColor };

  private draw?: TerritoryDrawController;
  /** Live area of the shape being drawn or reshaped, in km sq. "Is this patch
   *  the right size" is the question being answered, and a corner count does
   *  not answer it. */
  private drawingAreaSqKm = 0;
  get workingAreaSqKm(): number {
    return this.editing ? this.feature.editingAreaSqKm : this.drawingAreaSqKm;
  }
  private readonly destroy$ = new Subject<void>();

  private restoredTerritory?: { version?: number; selectedId: number | null; rosterOpen: boolean; boardOpen: boolean };
  private restoreTerritoryView(): void {
    const state = this.restoredTerritory;
    if (!state) return;
    if (state.version !== this.viewContext?.restorationVersion) { this.restoredTerritory = undefined; return; }
    const row = this.rows.find(item => item.territory.id === state.selectedId);
    if (state.selectedId !== null && !row) return;
    this.restoredTerritory = undefined;
    if (row) this.select(row, false);
    else this.clearSelection();
    this.rosterOpen = state.rosterOpen;
    this.showBoard(state.boardOpen);
  }

  private viewRegistration?: ContextSubscription;
  private registerViewContext(): void {
    this.viewRegistration = this.viewContext?.registerView({
      key: viewContextKey(this.viewRoute, 'Territories view'), name: 'Territories view', priority: -10,
      active: () => viewContextVisible(this.viewElement),
      read: () => ({ selectedId: this.selectedId, rosterOpen: this.rosterOpen, boardOpen: this.boardOpen }),
      describe: state => JSON.stringify(state),
      restore: state => { if (this.session?.dirty) return; this.restoredTerritory = { ...state, version: this.viewContext?.restorationVersion }; this.restoreTerritoryView(); },
    });
  }

  constructor(
    private http: HttpClient,
    auth: AuthService,
    private feature: TerritoryFeature,
    private featureManager: MapFeatureManagerService,
    private interactions: MapInteractionController,
    private editSessions: EditSessionRegistry,
    private dialog: MatDialog,
    private router?: Router,
    @ContextOptional() private readonly viewContext?: AppContextService,
    @ContextOptional() private readonly viewRoute?: ContextRoute,
    @ContextOptional() private readonly viewElement?: ContextElement<HTMLElement>,
  ) {
    // Resolved in the constructor: "is this patch mine" is list logic and must
    // hold before anything is drawn.
    this.myUserId = auth.tenant.currentUserId;
  }

  ngOnInit(): void {
    this.registerViewContext();
    this.buildFilterFields();   // the Status rule needs no roster to be listed
    // Clicking the map itself, away from any boundary, lets a patch go. Without
    // it the only way out of a selection was a control in the panel, and the
    // page felt stuck in whatever was last clicked.
    this.interactions.backgroundClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe(this.onMapClick);

    this.http.get<AssignableRep[]>('/api/territories/assignable-reps').subscribe({
      next: rows => {
        this.reps = rows;
        this.buildFilterFields();   // the Rep rule lists the roster it just got
        this.buildProspectFilterFields();   // and so does the leads table's
        // The map writes the rep's name on each patch, and the roster is where
        // the names are. Pushed rather than fetched twice.
        this.feature.setRepNames(new Map(
          rows.map(rep => [rep.userId, rep.displayName || rep.userName || `user ${rep.userId}`])));
      },
      error: () => { /* no picker: the id field still works */ },
    });

    // The Stage and Types rules on the leads table under a patch list the
    // tenant's own stages and categories, as the Leads page's do. A failed
    // read leaves that dropdown empty, not the table unusable.
    this.http.get<LeadStage[]>('/api/lead-stages').subscribe({
      next: rows => {
        this.leadStages = rows.filter(row => row.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        this.buildProspectFilterFields();
      },
      error: () => { /* the Stage rule simply has nothing to offer */ },
    });
    this.http.get<{ id: number; name: string }[]>('/api/lookup/business-types').subscribe({
      next: rows => { this.businessTypes = rows; this.buildProspectFilterFields(); },
      error: () => { /* the Types rule simply has nothing to offer */ },
    });

    this.load();
  }

  /** Bound once and removed on destroy: a leaked handler on the shared map
   *  would clear selections on every other page. */
  private readonly onMapClick = () => {
    // Drawing owns the map's clicks; they are corners, not dismissals.
    if (this.drawing) return;
    if (this.selectedId != null) this.clearSelection();
  };

  ngOnDestroy(): void {
    this.restoredTerritory = undefined;
    this.viewRegistration?.unsubscribe();
    // Gestures back before the layer: an abandoned draw must not leave click
    // handlers on a map that outlives this page.
    this.cancelDraw();
    this.destroy$.next();
    this.destroy$.complete();
    // Unregisters from the unsaved-changes guard. Forgetting this leaves a
    // permanently dirty entry that blocks navigation everywhere afterwards.
    this.stopEditing();
    this.session?.destroy();
    this.session = null;
    // A printout's frame belongs to this visit, not to the document it hangs off.
    this.printFrame?.remove();
    this.printFrame = null;
  }

  /** True while a boundary is being drawn. */
  get drawing(): boolean { return this.draw?.active ?? false; }

  /** Vertices placed so far — the template shows the count. */
  get draftPoints(): ReadonlyArray<unknown> { return this.draw?.points ?? []; }

  /** The roster, optionally narrowed to what I own. */
  get visibleRows(): TerritoryWithMetrics[] {
    const mine = this.mineOnly && this.myUserId != null
      ? this.rows.filter(r => r.territory.assignedUserId === this.myUserId)
      : this.rows;

    if (!this.anyFilter) return mine;

    // A Rep rule is dropped while Mine is on: both narrow the same column, and
    // answering both would AND them into an empty roster rather than answering
    // either question. The Leads board drops its Owner rule under "Assigned to
    // me" for the same reason.
    const values = this.mineOnly && this.myUserId != null
      ? { ...this.filterValues, rep: null }
      : this.filterValues;

    return mine.filter(row => matchesFilters(row, values, this.filterReaders));
  }

  /**
   * Whether the roster is showing every patch while one of them is selected.
   *
   * Picking a patch opens a detail panel above this list that is taller than
   * anything else on the page, and the list then pushes it out of the window -
   * so the moment you choose something, the thing you chose it to see is off
   * screen. Folded, the roster is one row plus a way back.
   *
   * It is a fact about this VIEW, not about the selection: nothing else keys
   * off it, and clearing the selection puts the whole list back without
   * anybody having to remember to reset it.
   */
  private rosterOpen = false;

  /**
   * The rows the roster draws.
   *
   * Everything, unless a patch is selected and the list has not been asked
   * for - then only that patch, with the rest behind the count below it. The
   * selected row stays because the list is also where its colour, status and
   * numbers live, and folding it to nothing would move those.
   */
  get rosterRows(): TerritoryWithMetrics[] {
    const all = this.visibleRows;
    if (this.selectedId == null || this.rosterOpen) return all;
    // The open patch is already named, counted and measured by the section
    // below, so the roster does not say it a second time: it folds away
    // whole. Leaving the picked row in printed the same territory twice, one
    // line above the other, which is what this was reported as.
    const picked = all.some(row => row.territory.id === this.selectedId);
    // A selection filtered out of view - "Mine" is on and this one is not
    // mine - would fold to nothing with nothing below it either. Show the
    // list instead.
    return picked ? [] : all;
  }

  /** How many patches the fold is hiding. Zero when nothing is folded. */
  get foldedAway(): number {
    return this.visibleRows.length - this.rosterRows.length;
  }

  /** The way back to the whole list. It does not fold again from here: the
   *  next patch picked does that, which is the moment the room is wanted. */
  openRoster(): void { this.rosterOpen = true; }

  get canFilterMine(): boolean {
    return this.myUserId != null && this.rows.some(r => r.territory.assignedUserId === this.myUserId);
  }

  /** Totals for whatever the roster is showing - "what is my patch worth". */
  get visibleTotals(): { leads: number; openCases: number; pipeline: number; won: number } {
    return this.visibleRows.reduce((acc, r) => ({
      leads: acc.leads + r.leads,
      openCases: acc.openCases + r.openCases,
      pipeline: acc.pipeline + r.openPipelineValue,
      won: acc.won + r.won,
    }), { leads: 0, openCases: 0, pipeline: 0, won: 0 });
  }

  repLabel(userId: number | null): string {
    if (userId == null) return 'unassigned';
    const rep = this.reps.find(r => r.userId === userId);
    return rep?.displayName || rep?.userName || `user ${userId}`;
  }

  // ── Export and Print ───────────────────────────────────────────────────

  /**
   * Which of the two is reading leads, or null.
   *
   * One at a time, and both buttons wait: each walks every chosen territory
   * a server page at a time, and a second walk beside the first would only
   * double the load for the same answer.
   */
  sheetBusy: SheetAction | null = null;
  sheetError: string | null = null;

  /** The frame the last printout was written into. Replaced by the next one
   *  and removed when the page is left. */
  private printFrame: HTMLIFrameElement | null = null;

  /** The server's own page cap, so a territory of a few hundred leads is one
   *  or two reads. */
  static readonly SheetPageSize = 200;

  /**
   * Whether Mine or a filter rule is narrowing the roster, which is the only
   * time "the ones shown" is a different set from all of them. The board is
   * drawn from the same rows, so it holds on either view.
   */
  get rosterNarrowed(): boolean {
    return this.visibleRows.length < this.rows.length;
  }

  /** The territories a scope names, as the roster holds them now. */
  sheetRows(scope: SheetScope): TerritoryWithMetrics[] {
    switch (scope) {
      case 'selected': return this.selected ? [this.selected] : [];
      case 'shown': return this.visibleRows;
      default: return this.rows;
    }
  }

  /** The scope in words: the menu offers it and the printout is headed with it. */
  sheetScopeLabel(scope: SheetScope): string {
    const count = this.sheetRows(scope).length;
    switch (scope) {
      case 'selected': return this.selected?.territory.name ?? '';
      case 'shown': return `The ${count} shown`;
      default: return `All ${count} ${count === 1 ? 'territory' : 'territories'}`;
    }
  }

  /**
   * Export or print the chosen territories, with every lead in each.
   *
   * The leads are read here rather than taken from the table under an open
   * patch, which holds one page of one territory. Nothing is written until
   * every territory has answered: a file or a sheet missing the leads of the
   * one that failed would read as a territory with none.
   */
  runSheets(action: SheetAction, scope: SheetScope): void {
    const chosen = this.sheetRows(scope);
    if (this.sheetBusy || !chosen.length) return;
    // Taken now: the roster can change while the leads are being read.
    const label = this.sheetScopeLabel(scope);
    this.sheetBusy = action;
    this.sheetError = null;
    from(chosen).pipe(
      concatMap(row => this.sheetLeads(row).pipe(map(leads => ({ row, leads })))),
      toArray(),
      takeUntil(this.destroy$),
    ).subscribe({
      next: sheets => {
        this.sheetBusy = null;
        const rep = (userId: number) => this.repLabel(userId);
        if (action === 'export') {
          triggerBlobDownload(
            new Blob([territorySheetsCsv(sheets, rep)], { type: 'text/csv;charset=utf-8' }),
            territorySheetsFilename(sheets, todayLocalDateString()));
        } else {
          this.printDocument(territorySheetsHtml(sheets, {
            rep, scope: label, printedAt: new Date(), unsavedChanges: this.dirty,
          }));
        }
      },
      error: () => {
        this.sheetBusy = null;
        this.sheetError = 'Could not read the leads of every territory, so nothing was '
          + `${action === 'export' ? 'exported' : 'printed'}.`;
      },
    });
  }

  /**
   * Every lead in one territory, in name order, a server page at a time.
   *
   * Sorted so the walk is stable - the server breaks ties by id - and so a
   * name is where a reader looks for it. Pages are counted by what came back
   * rather than by what was asked for, so a server that caps a page lower is
   * still walked to the end, and an empty page ends it.
   *
   * A territory only drawn on this page has no id the server knows, so
   * nothing is asked for it.
   */
  private sheetLeads(row: TerritoryWithMetrics): Observable<WorkItem[]> {
    if (row.territory.id < 0) return of([]);
    const read = (page: number, before: number): Observable<WorkItem[]> =>
      this.http.get<LeadWorkItemList>('/api/lead-work-items', {
        params: new HttpParams()
          .set('territoryId', row.territory.id)
          .set('page', page)
          .set('pageSize', TerritoriesComponent.SheetPageSize)
          .set('sort', 'title'),
      }).pipe(concatMap(res => {
        const upTo = before + res.items.length;
        return res.items.length && upTo < res.total
          ? read(page + 1, upTo).pipe(map(rest => res.items.concat(rest)))
          : of(res.items);
      }));
    return read(1, 0);
  }

  /**
   * Hand a document to the browser's print dialog without leaving the page.
   *
   * Written into a hidden frame. A new window is something a popup blocker
   * stops, and it leaves a tab behind; printing this page itself would take
   * print styles hiding the map, the sidebar and the shell, and would still
   * print one open patch's page of leads at best.
   */
  private printDocument(html: string): void {
    this.printFrame?.remove();
    const frame = document.createElement('iframe');
    frame.className = 'tr-print-frame';
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    const onLoad = () => {
      // A frame opens on an empty document of its own; print the one written in.
      if (frame.contentWindow?.location.href !== 'about:srcdoc') return;
      frame.removeEventListener('load', onLoad);
      frame.contentWindow.print();
    };
    frame.addEventListener('load', onLoad);
    frame.srcdoc = html;
    document.body.appendChild(frame);
    this.printFrame = frame;
  }

  // ── Data ───────────────────────────────────────────────────────────────

  /**
   * The unsaved map. Null until a load succeeds - there is nothing to edit
   * before then, and the Save bar must not appear over an empty page.
   *
   * One session for the page: the roster and the map both read `session.state`,
   * so a boundary the admin has dragged but not saved cannot show on one and
   * not the other.
   */
  session: EditSession<DraftState, DraftOp> | null = null;

  /** Named territories somebody else moved under us. Set from a 409. */
  conflicts: TerritoryConflict[] = [];
  saveSummary: string | null = null;

  /**
   * Re-reads the server and adopts the answer as the session's new baseline,
   * which drops every staged edit.
   *
   * There is NO Refresh button on the toolbar any more, and that is why. It
   * was a discard wearing the clothes of a reload, sitting first on the action
   * line where it was the easiest thing on the page to hit; it needed a
   * disabled state and a tooltip explaining the disabled state to be safe at
   * all. The page loads itself, and the one thing a person could want it for -
   * picking up someone else's change after a conflict - is the button inside
   * the conflict notice, where the sentence beside it says what will happen.
   */
  isPicked(id: number): boolean { return this.mergePicks.includes(id); }

  /** Ticked and unticked in order: the first stays, the rest are folded in. */
  togglePick(id: number): void {
    const at = this.mergePicks.indexOf(id);
    if (at >= 0) this.mergePicks.splice(at, 1);
    else this.mergePicks.push(id);
  }

  clearPicks(): void { this.mergePicks = []; }

  /** The one that keeps its name and everything else about it. */
  get mergeSurvivor(): TerritoryWithMetrics | null {
    const first = this.mergePicks[0];
    return first === undefined
      ? null
      : this.rows.find(row => row.territory.id === first) ?? null;
  }

  mergeNow(): void {
    if (this.merging || this.mergePicks.length < 2) return;
    const [survivor, ...absorb] = this.mergePicks;
    this.merging = true;
    this.saveError = null;
    this.mergeNote = null;
    this.http
      .post<TerritoryMergeResult>(`/api/territories/${survivor}/merge`, { absorb })
      .subscribe({
        next: res => {
          this.merging = false;
          this.lastMerge = res;
          this.mergePicks = [];
          const count = res.absorbed.length;
          this.mergeNote =
            `${count} ${count === 1 ? 'territory' : 'territories'} merged into `
            + `${res.survivor.name}, with ${res.leadsMoved} `
            + `${res.leadsMoved === 1 ? 'lead' : 'leads'}.`;
          this.load();
        },
        error: err => {
          this.merging = false;
          this.saveError = err.status === 403
            ? 'You do not have permission to merge territories.'
            : 'Could not merge those territories. Nothing was changed.';
        },
      });
  }

  undoMerge(): void {
    const merged = this.lastMerge;
    if (!merged || this.merging) return;
    this.merging = true;
    this.saveError = null;
    this.http
      .post<void>(`/api/territories/${merged.survivor.id}/merge/undo`, merged)
      .subscribe({
        next: () => {
          this.merging = false;
          this.lastMerge = null;
          this.mergeNote = null;
          this.load();
        },
        error: () => {
          this.merging = false;
          this.saveError = 'Could not undo the merge.';
        },
      });
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.http.get<TerritoryWithMetrics[]>('/api/territories/metrics').subscribe({
      next: res => {
        this.adoptRows(res);
        this.loading = false;
      },
      error: () => {
        this.loadError = 'Failed to load territories.';
        this.loading = false;
      },
    });
  }

  /**
   * Adopt rows just read from the server: they become the session's baseline,
   * and the map is drawn from them. Every load goes through here so the roster,
   * the map and the undo stack cannot be seeded from different reads.
   *
   * `reload` rather than a new session when one exists - a swapped instance
   * leaks its registration and leaves a permanently dirty entry that blocks
   * navigation off every later page.
   */
  private adoptRows(rows: TerritoryWithMetrics[]): void {
    const state: DraftState = { rows, edits: [], nextTempId: -1 };
    if (this.session) {
      this.session.reload(state);
    } else {
      this.session = new EditSession<DraftState, DraftOp>({
        initial: state,
        reduce: reduceDraft,
        registry: this.editSessions,
        commit: draft => this.commitDraft(draft),
      });
      this.session.state$
        .pipe(takeUntil(this.destroy$))
        .subscribe(next => this.onDraftChanged(next));
    }
    this.rows = rows;
    this.restoreTerritoryView();
    this.feature.setRows(rows);
    this.conflicts = [];
  }

  /** The roster and the map follow the session, so neither can show a boundary
   *  the other does not. */
  private onDraftChanged(draft: DraftState): void {
    this.rows = draft.rows;
    this.feature.setRows(draft.rows);
  }

  /**
   * Save. One request for the whole draft; the server resolves it exactly as
   * the preview did, so what the admin approved is what lands.
   */
  private commitDraft(draft: DraftState): Observable<DraftState> {
    this.conflicts = [];
    return this.http
      .post<DraftCommit>('/api/territories/draft/commit', toDraftInput(draft))
      .pipe(
        map(result => {
          this.saveSummary = result.stopsReassigned > 0
            ? `Saved. ${result.stopsReassigned} lead${result.stopsReassigned === 1 ? '' : 's'} changed hands.`
            : 'Saved.';
          // The commit returns the committed rows, so the new baseline needs no
          // follow-up GET - which would race the admin's next edit.
          this.rows = result.territories;
          this.feature.setRows(result.territories);
          return { rows: result.territories, edits: [], nextTempId: -1 };
        }),
        catchError(err => {
          if (err.status === 409 && err.error?.conflicts) {
            this.conflicts = err.error.conflicts;
          }
          return throwError(() => err);
        }));
  }

  // -- Editing ------------------------------------------------------------

  /** Whether anything is staged. Drives the Save bar and the guard. */
  get dirty(): boolean { return this.session?.dirty ?? false; }

  private apply(op: DraftOp): void {
    this.session?.apply(op);
  }

  /** A successful save is a new baseline; the commit already returned the rows,
   *  so there is nothing to re-fetch. */
  onDraftSaved(): void {
    this.conflicts = [];
    this.clearSelection();
  }

  /** Why a lead moved, in words. "The map changed" and "you moved it" must never
   *  look like the same event. */
  moveReason(reason: string): string {
    switch (reason) {
      case 'PolygonEdit': return 'the boundary moved';
      case 'NoLongerCovered': return 'no territory covers it';
      case 'Added': return 'newly added';
      case 'Removed': return 'removed';
      default: return reason;
    }
  }

  private nameOf(key: DraftKey): string {
    const row = this.rows.find(r => {
      const k = keyOf(r);
      return ('id' in k && 'id' in key && k.id === key.id)
          || ('tempId' in k && 'tempId' in key && k.tempId === key.tempId);
    });
    return row?.territory.name ?? 'territory';
  }

  get selected(): TerritoryWithMetrics | null {
    return this.rows.find(r => r.territory.id === this.selectedId) ?? null;
  }

  select(row: TerritoryWithMetrics, locate = true): void {
    this.restoredTerritory = undefined;
    this.stopEditing();
    if (this.drawing) this.cancelDraw();
    this.selectedId = row.territory.id;
    // Fold on every pick, including a pick made from the opened list: the
    // detail panel is what was just asked for, and it needs the room.
    this.rosterOpen = false;
    // A complaint raised about the patch just left is not about this one. It used
    // to survive the switch, so a territory with no boundary went on saying so
    // over every territory selected after it.
    this.saveError = null;
    this.boundaryIssue = null;
    this.fillForm(row);
    this.feature.selection.replace([row.territory.id]);
    if (locate) this.feature.zoomTo(row.territory);
    this.editingField = null;
    this.loadProspects();
    const boundary = row.territory.boundaryGeoJson;
    this.boundaryIssue = !boundary?.trim() ? 'missing'
      : ringsOf(boundary).length ? null : 'unusable';
  }

  /** The form mirrors the saved row. Extracted so abandoning an inline edit can
   *  put back exactly what was there. */
  private fillForm(row: TerritoryWithMetrics): void {
    this.form = {
      name: row.territory.name,
      status: row.territory.status,
      assignedUserId: row.territory.assignedUserId,
      color: row.territory.color ?? '',
    };
  }

  clearSelection(): void {
    this.restoredTerritory = undefined;
    this.stopEditing();
    this.rosterOpen = false;
    this.boundaryIssue = null;
    this.selectedId = null;
    this.resetForm();
    this.feature.selection.clear();
  }

  private resetForm(): void {
    const counts = new Map<string, number>();
    for (const row of this.rows) {
      const color = territoryPaletteColor(row.territory.color);
      counts.set(color, (counts.get(color) ?? 0) + 1);
    }
    const color = this.palette.reduce((best, candidate) =>
      (counts.get(candidate) ?? 0) < (counts.get(best) ?? 0) ? candidate : best);
    this.form = { name: '', status: 'Draft', assignedUserId: null, color };
  }

  // ── Drawing (self-implemented) ─────────────────────────────────────────

  /**
   * Press and drag the shape you mean; letting go closes it, simplifies the
   * trace, seams it onto whatever it runs along and opens it for adjusting.
   *
   * The only way in. Placing corners one click at a time used to sit beside it;
   * sketching and then dragging the handles the editor puts on every corner
   * reaches the same shape, and the second mode cost a second set of map
   * gestures to keep from outliving the first.
   */
  startFreehand(redrawId: number | null = null): void {
    this.beginDraw(redrawId);
  }

  private beginDraw(redrawId: number | null): void {
    this.redrawTargetId = redrawId;
    this.saveError = null;
    this.boundaryIssue = null;
    this.seamNote = null;
    this.stopEditing();
    // The draw controller and the normal selection controller listen to the
    // same pointer stream. Give the draw session exclusive ownership until it
    // completes or is cancelled.
    this.interactions.beginEdit();
    // Built on first use rather than at construction: the shared map is
    // guaranteed to exist by the time a rep can reach the Draw button.
    this.draw ??= this.interactions.createSession(map =>
      new TerritoryDrawController(
        map,
        geoJson => {
          this.interactions.endEdit();
          this.noteSeams();
          this.save(this.redrawTargetId, geoJson);
        },
        () => { this.drawingAreaSqKm = this.draftAreaSqKm(); },
      ));
    // Snapped onto every OTHER boundary. Patches may not overlap, and two drawn
    // freehand never share an edge - they cross by a metre somewhere, and the
    // save then reports a repair and moves somebody's border.
    this.draw.start(this.form.color, this.ringsExcept(redrawId));
    this.applyMapCursor();
  }

  /** True while a freehand sketch is under the pointer, before it is released. */
  get sketching(): boolean { return this.draw?.sketching ?? false; }

  /** Why a finished outline was refused, if it was. Kept on screen with the
   *  shape that caused it, so it can be fixed instead of redrawn. */
  get drawProblem(): string | null { return this.draw?.problem ?? null; }

  /** True while the trace is running along a neighbouring boundary. */
  get snapping(): boolean { return this.draw?.snapping ?? false; }

  /** True while Alt is held and the trace is being taken exactly as drawn. */
  get snapSuspended(): boolean { return this.draw?.snapSuspended ?? false; }

  /**
   * What the last sketch shared with its neighbours, said once it is closed.
   *
   * A seam is invisible once made - that is the point of it - so the only moment
   * it can be reported is the moment it is made. The zero case is the one worth
   * saying: a patch that shares no edge is the one whose save can still report a
   * repair.
   */
  seamNote: string | null = null;

  private noteSeams(): void {
    if (!this.draw) return;
    const { adopted, seams, pulled, closed } = this.draw.seamStats;
    const said: string[] = [];
    // The overlap correction goes first: it changed the shape more than the
    // seam did, and it is the one somebody might otherwise think was their own
    // drawing.
    if (pulled) {
      said.push(`${pulled} corner${pulled === 1 ? '' : 's'} had landed inside a ` +
        `neighbour and ${pulled === 1 ? 'was' : 'were'} moved onto its border`);
    }
    // Said out loud for the same reason: the shape grew slightly, and an admin
    // who is not told will read it as their own stroke.
    if (closed) {
      said.push(`${closed} corner${closed === 1 ? '' : 's'} stopped short of a neighbour, so ` +
        `the gap${closed === 1 ? ' was' : 's were'} absorbed into this boundary`);
    }
    if (adopted) {
      said.push(`sharing ${adopted} corner${adopted === 1 ? '' : 's'} with ` +
        `${seams} neighbouring boundar${seams === 1 ? 'y' : 'ies'}, so those edges match exactly`);
    }
    this.seamNote = said.length ? said.join('; ') + '.' : null;
  }

  private draftAreaSqKm(): number {
    // The traced sketch as well as the placed corners: "is this patch the right
    // size" is the question the readout answers, and during a freehand drag it is
    // the only moment the answer can still change the gesture.
    const points = (this.draw?.outline ?? []) as L.LatLng[];
    return points.length >= 3 ? ringAreaSqMeters(points) / 1_000_000 : 0;
  }

  // ── Reshaping an existing boundary ──────────────────────────────────────

  /** True while the corners of a boundary are draggable. */
  get editing(): boolean { return this.feature.editing; }
  get editVertexCount(): number { return this.feature.editVertexCount; }
  get canUndoEdit(): boolean { return this.feature.canUndoEdit; }
  get canEditBoundary(): boolean {
    return !this.drawing && this.selectedId != null
      && this.feature.canEdit(this.selectedId);
  }

  /** True once the shape on the map differs from the saved one. */
  get shapeChanged(): boolean { return this.feature.shapeChanged; }

  /**
   * What the mouse is for right now. One value drives the cursor and which
   * keys do anything, so the two can never describe different states. It drove
   * a third thing, the hint line under the toolbar, until that went.
   */
  get mode(): 'draw' | 'edit' | 'browse' {
    if (this.drawing) return 'draw';
    if (this.editing) return 'edit';
    return 'browse';
  }

  // There is no standing hint line on this page.
  //
  // There were two, then one, and now none. What the last one said while a
  // boundary was open - drag a node, drag an edge, right-click to remove -
  // is a caption for a gesture somebody is already making: by the time the
  // handles are under the cursor, the sentence is describing what the person
  // has just done. And it sat directly under the toolbar, so every state that
  // had something to say pushed the map and the roster down a line to say it.
  //
  // What survived is on the controls themselves: Draw Territory's tooltip
  // says how tracing works, the toolbar counts nodes and says when a shape is
  // not saved, and Cancel and Close name the key that reaches them.

  /**
   * Puts the mode on the map container so the cursor can follow it.
   *
   * On the container rather than in a component stylesheet because the map is
   * the app shell's, outside this component's encapsulation - the same reason
   * the handle styles are global.
   */
  private applyMapCursor(): void {
    this.interactions.setContainerMode(
      ...(this.mode === 'draw'
        ? ['tr-mode-draw']
        : this.mode === 'edit'
          ? ['tr-mode-edit']
          : []),
    );
  }

  /** Exit the editor while retaining the selected object and page-staged edits. */
  doneEditing(): void { this.stopEditing(); }

  /** Explicit entry point: selection and ordinary inspection never start editing. */
  startEditing(): void {
    if (!this.canEditBoundary || this.selectedId == null || this.editing) return;
    this.feature.beginEdit(this.selectedId);
    this.applyMapCursor();
  }


  // ── Leads in this territory ─────────────────────────────────────────────

  /** The list under the territory's details. Server-paged: a patch can hold
   *  thousands of doors, and the page shows one screenful with a scrollbar. */
  get prospectTotal(): number { return this.prospectTable?.total ?? this.compactList?.total ?? 0; }
  readonly prospectPageSize = 50;
  /** A one-line note under the list, for what was just staged. */
  /** Selected by Lead id, so a selection made on page one survives a look
   *  at page two. */

  /** The shared table, so a staged add or removal can make it read again. */
  @ViewChild('prospectTable') prospectTable?: DataTableComponent<WorkItem>;

  /**
   * The Leads list, and it is the SAME list /leads/table shows.
   *
   * It used to be three columns - name, address, pinned - on the argument that
   * this frame is about a third as wide as the leads table and the rest was
   * one click away on the lead page. In use that argument lost: a rep redrawing
   * a boundary is deciding which doors belong to whom, and stage, rep,
   * contribution and next action are exactly what that decision is made on.
   * Sending them to another page to read each one, one lead at a time, is the
   * complaint this answers.
   *
   * The width problem was real, so it is solved where it belongs - in the
   * column picker. `prospectHiddenColumns` below is the opening set; every
   * column is one tick away and the choice is remembered per user.
   */
  readonly prospectColumns: readonly DataTableColumn[] = LEAD_LIST_COLUMNS;

  /**
   * What the list opens on, beside a map, in a third of a page.
   *
   * The six left showing are the ones a boundary is argued over: which lead,
   * where it is, what stage it is at, and whose it is. The other eleven are a tick away in the picker and stay ticked.
   */
  readonly prospectHiddenColumns: readonly string[] = [
    'state', 'postalCode', 'origin', 'sources', 'businessTypes', 'score',
    'contribution', 'routeFit', 'nextAction', 'probability', 'discoveredAt',
  ];

  /**
   * Whether the name is being typed into.
   *
   * The territory's name, code, status, rep and branch used to be five
   * permanent input boxes. Five boxes to display five short strings is a form
   * pretending to be a heading. Status and rep have since become dropdowns,
   * which are controls whether or not anybody is using one; the name is the
   * one that is still read far more often than it is written, so it is text
   * until it is clicked.
   */
  editingField: DetailField | null = null;

  editField(field: DetailField): void {
    this.editingField = field;
  }

  /** Leaves the field and stages the change, so there is no Apply to remember.
   *  A rename is one edit, not one edit and a confirmation. */
  commitField(): void {
    if (!this.editingField) return;
    this.editingField = null;
    this.saveDetails();
  }

  /** Abandons the edit and puts back what was there. */
  cancelField(): void {
    const row = this.selected;
    if (row) this.fillForm(row);
    this.editingField = null;
  }

  // -- The leads table's filter bar ----------------------------------------
  //
  // The Leads page's own columns (lead-filters.ts), because this is the Leads
  // page's own table: a second list written here is how the two would stop
  // matching each other and the server. The roster's bar filters patches;
  // this one filters the leads inside the open one.

  private leadStages: LeadStage[] = [];
  private businessTypes: { id: number; name: string }[] = [];

  /** Every column from the start, so a rule applied before the dropdowns'
   *  options land still reaches the server; rebuilt as each list arrives. */
  prospectFilterFields: DataFilterField[] = leadRuleFilterFields([], [], []);
  prospectFilterValues: DataFilterValues = {};

  private buildProspectFilterFields(): void {
    this.prospectFilterFields =
      leadRuleFilterFields(this.businessTypes, this.leadStages, this.reps);
  }

  /** The bar applied its values. A new object is the table's signal to read
   *  its first page again under them. */
  applyProspectFilters(values: DataFilterValues): void {
    this.prospectFilterValues = values;
  }

  /**
   * The leads in the selected patch, from the endpoint /leads/table reads.
   *
   * The same list, filtered to one territory - `territoryId` is a filter that
   * endpoint already took. This page used to read `/territories/draft/stops`
   * instead, which returns five fields, and that is why the table under a
   * patch showed three columns while the leads table showed sixteen. Reading
   * the leads list here rather than widening the stops query also keeps one
   * projection: qualification, route fit and contribution are each a snapshot
   * lookup, and a second copy of that is a second thing to keep true.
   *
   * An arrow property, not a method: the table calls it with no receiver, and
   * a method would arrive with `this` undefined.
   *
   * No territory selected is an empty page rather than a request. The table
   * mounts before anything is picked, and asking the server for the leads of
   * nothing would be one wasted round trip per visit.
   */
  readonly fetchProspects: DataFetch<WorkItem> = query => {
    const row = this.selected;
    if (!row) return of({ items: [], total: 0 });
    // A patch that has only been sketched has no id the server knows. Asking
    // about a negative one is a round trip whose only answer is an empty page.
    if (row.territory.id < 0) {
      return of({ items: [], total: 0 });
    }

    let params = new HttpParams()
      .set('territoryId', row.territory.id)
      .set('page', query.page)
      .set('pageSize', query.pageSize);
    const search = (query.search ?? '').trim();
    if (search) params = params.set('search', search);
    // `query.sort` is already the server's key: the header is bound to the
    // column's sortKey, not to its id.
    if (query.sort) params = params.set('sort', query.sort).set('desc', query.desc ?? false);
    // The bar's rules, one repeated `rule` each - the grammar /leads/table
    // sends to this same endpoint, which holds the whitelist.
    for (const rule of toRuleParams(query.filters, this.prospectFilterFields))
      params = params.append('rule', rule);

    // The shared view owns loaded rows, total and retry state. Do not turn a
    // failed next-page request into an empty successful end of the list.
    return this.http.get<LeadWorkItemList>('/api/lead-work-items', { params });
  };

  /** Reload through the table, so its page, search and selection stay its own. */
  loadProspects(): void {
    this.prospectTable?.reload();
    this.compactList?.reload();
  }

  /** The table owns the tick boxes; this page owns what Remove Selected acts on. */

  /**
   * Open a lead the way the leads table opens one: the routed lead page, which
   * is where every field this frame cannot show already lives. It used to be
   * that a row here did nothing but tick itself, so the only way to read a
   * lead you found on the map was to go and find it again somewhere else.
   */
  openLead(row: WorkItem): void {
    this.router?.navigate(['/leads/table', row.leadId]);
  }

  // ── Leads list cells ──────────────────────────────────────────────────
  //
  // The same readings the leads table gives these columns, so a lead does not
  // describe itself one way on /leads/table and another under its patch.

  /** The lead's pipeline stage colour. Not `statusColor`, which is the
   *  TERRITORY's status - two different words that both read as "status". */
  leadStageColor(code: string | null | undefined): string {
    return stageColor(code ?? '');
  }

  typeLabels(names: string[] | null | undefined): string {
    return businessTypeLabels(names ?? []).join(', ');
  }

  sourceLabel(row: WorkItem): string {
    return leadSourceLabel(row.sourceKinds);
  }

  overdue(row: WorkItem): boolean {
    return leadActionOverdue(row.nextActionDue);
  }

  /**
   * LIST OR BOARD, for the leads of the territory that is open.
   *
   * The list is the table this page has always had: every column, sorting,
   * paging, and the row actions. The board is the same leads as cards, in
   * columns that ARE the territories.
   *
   * Not two pages: the same leads, one toggle. A board
   * that lived somewhere else would be a second place for the same facts to
   * be wrong in.
   */
  boardOpen = false;

  /** One column per territory: the patch, its leads, and whether they are in
   *  yet. Built on demand - the list costs nothing to leave loaded. */
  boardColumns: {
    row: TerritoryWithMetrics;
    leads: WorkItem[];
    total: number;
    loading: boolean;
  }[] = [];

  /**
   * How many cards a column shows.
   *
   * A column is for reading, not for auditing: past a
   * screenful nobody is looking, and a company with thirty patches would
   * otherwise open thirty full pages at once. The count under the heading is
   * the whole truth.
   */
  static readonly BoardCards = 50;

  /**
   * The page takes the window while the board has it.
   *
   * The missing link the first attempt at this did not have. `.ws-card-body`
   * - the outlet's container - is a bounded flex column, and the leads board
   * reaches it through `:host { height: 100% }`. This host had no height at
   * all, so `workspace-page--full` resolved its own 100% against nothing, the
   * panel's overflow:hidden clipped what followed, and the board vanished.
   *
   * Only while the board has the page: the roster and the open patch below
   * are an ordinary document that reads top to bottom, and bounding those
   * would wrap a second scrollbar around it.
   */
  @HostBinding('class.tr-windowed') get windowed(): boolean {
    return this.boardOpen;
  }

  /**
   * Open the board, or go back to the list.
   *
   * It takes the page rather than sitting inside the open patch, which is
   * where it used to be: a column per territory answers a question about all
   * of them, and reaching it meant choosing one first - on a page that opens
   * with nothing chosen. Reached from the roster's own header now, where the
   * patches are.
   */
  showBoard(open: boolean): void {
    this.boardOpen = open;
    if (open) this.loadBoard();
  }

  private loadBoard(): void {
    const columns = this.visibleRows.filter(r => r.territory.id > 0);
    this.boardColumns = columns.map(row => ({
      row, leads: [], total: 0, loading: true,
    }));
    for (const column of this.boardColumns) {
      const params = new HttpParams()
        .set('territoryId', column.row.territory.id)
        .set('page', 0)
        .set('pageSize', TerritoriesComponent.BoardCards);
      this.http.get<LeadWorkItemList>('/api/lead-work-items', { params })
        .subscribe({
          next: page => {
            column.leads = page.items;
            column.total = page.total;
            column.loading = false;
          },
          error: () => { column.loading = false; },
        });
    }
  }

  /**
   * A card dropped into another territory's column.
   *
   * The card moves on screen straight away and the assignment is staged, in
   * that order: a card that snaps back while a request settles reads as a
   * refusal. Nothing is written until Save the map, so the optimistic move
   * and the truth are never more than one Cancel apart.
   */

  /** The ticked leads, as a list. A template cannot spread a Set. */

  undoEdit(): void { this.feature.undoEdit(); }

  cancelEditing(): void { this.stopEditing(); }

  /**
   * Stages the reshaped outline. It joins the session as a redraw, exactly as a
   * click-drawn one does - so the preview, the undo strip and the one Save at
   * the top treat a dragged corner and a fresh outline the same way.
   */
  applyReshape(): void {
    const row = this.selected;
    if (!this.editing || !row) return;

    // One redraw per shape that moved, neighbours included. A shared border
    // that shifted is a change to BOTH territories; staging only the one under
    // the cursor would save the new line on one side and the old line on the
    // other, which is the gap this feature exists to close.
    const changed = this.feature.changedShapes;

    // Refused with the handles still up, so the tangle can be pulled out of the
    // shape that has it. Dropping out of editing first would leave somebody
    // reading "this crosses itself" next to a boundary they can no longer touch.
    const problem = changed.map(shape => validateRings(shape.rings)).find(p => p !== null);
    if (problem) { this.saveError = problem; return; }

    this.saveError = null;
    this.stopEditing();

    for (const shape of changed) {
      const key: DraftKey = shape.id < 0 ? { tempId: shape.id } : { id: shape.id };
      this.apply({
        kind: 'redraw', key, boundaryGeoJson: ringsToGeoJson(shape.rings),
        label: shape.id === row.territory.id
          ? `Reshaped ${this.nameOf(key)}`
          : `Moved the border with ${this.nameOf(key)}`,
      });
    }

    // Apply stages the change but keeps the explicitly opened edit session active.
    this.startEditing();
  }

  private stopEditing(): void {
    this.feature.endEdit();
    this.drawingAreaSqKm = 0;
    this.applyMapCursor();
  }

  /** Every other boundary's rings, as snap targets. */
  private ringsExcept(id: number | null): L.LatLng[][] {
    return this.rows
      .filter(r => r.territory.id !== id)
      .flatMap(r => ringsOf(r.territory.boundaryGeoJson));
  }

  cancelDraw(): void {
    this.redrawTargetId = null;
    this.draw?.cancel();
    this.interactions.endEdit();
    this.applyMapCursor();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.drawing) this.cancelDraw();
    else if (this.editing) this.stopEditing();
    else if (this.selectedId != null) this.clearSelection();
  }

  // ── Save / delete / assignment ─────────────────────────────────────────

  /**
   * Stage the form. Nothing is written: the edit joins the session and the
   * server is asked what it WOULD do, which is the whole point of the draft -
   * editing a boundary moves every lead inside it into a different rep's book,
   * and an admin gets to see that before it is true.
   */
  save(id: number | null, boundaryGeoJson: string | null): void {
    const name = this.form.name.trim() || (id == null ? 'New territory' : '');
    if (id == null && !boundaryGeoJson) {
      this.saveError = 'Draw a boundary first.';
      return;
    }
    this.saveError = null;
    // The last gate before a boundary becomes a staged edit, whichever tool made
    // it. The draw controller already refuses an outline it cannot close, and the
    // server re-validates everything on both preview and commit; this catches a
    // ring that reached here by some other path and says so without a round trip.
    if (boundaryGeoJson) {
      const problem = validateBoundaryGeoJson(boundaryGeoJson);
      if (problem) { this.saveError = problem; return; }
    }
    const fields = {
      name: name || null,
      status: this.form.status,
      assignedUserId: this.form.assignedUserId,
      color: this.form.color || null,
    };

    if (id == null) {
      this.apply({
        kind: 'create', boundaryGeoJson: boundaryGeoJson!, fields,
        label: `Drew ${fields.name}`,
      });
      this.cancelDraw();
      // Select the newly staged object. Reshaping still requires an explicit Edit.
      const fresh = this.rows.filter(r => r.territory.id < 0)
        .sort((a, b) => a.territory.id - b.territory.id)[0];
      if (fresh) this.select(fresh);
      return;
    }

    const key: DraftKey = { id };
    if (boundaryGeoJson) {
      this.apply({
        kind: 'redraw', key, boundaryGeoJson,
        label: `Redrew ${this.nameOf(key)}`,
      });
    }
    this.apply({ kind: 'fields', key, fields, label: `Edited ${fields.name ?? this.nameOf(key)}` });
    this.cancelDraw();
  }

  /**
   * Hands one territory to a rep, from the row it is about.
   *
   * The plan deals reps out round-robin, which is a starting point and not a
   * decision - "this one to her" is the decision, and making it used to mean
   * opening the territory, finding the Rep field and editing it, once per
   * territory. The list is where you compare them, so it is where the
   * correcting belongs.
   *
   * Staged like every other edit on this page, so it joins the same Save and
   * Cancel takes it back with everything else.
   */
  assignRep(row: TerritoryWithMetrics, userId: number | null): void {
    if (row.territory.assignedUserId === userId) return;
    this.saveError = null;
    this.apply({
      kind: 'fields',
      key: keyOf(row),
      // The row's own values, with one changed. Reading them off `this.form`
      // would write whichever territory happens to be OPEN onto this one.
      fields: {
        name: row.territory.name,
        status: row.territory.status,
        assignedUserId: userId,
        color: row.territory.color ?? null,
      },
      label: userId == null
        ? `Unassigned ${row.territory.name}`
        : `Gave ${row.territory.name} to ${this.repLabel(userId)}`,
    });
  }

  saveDetails(): void {
    if (this.selectedId == null) return;
    this.save(this.selectedId, null);
  }

  /** Stage a removal. The row leaves the roster; nothing is deleted until Save. */
  remove(row: TerritoryWithMetrics): void {
    const key = keyOf(row);
    this.saveError = null;
    this.apply({ kind: 'delete', key, label: `Removed ${row.territory.name}` });
    if (this.selectedId === row.territory.id) this.clearSelection();
  }

  /** Re-derives every non-manual assignment from the current boundaries. */

  /**
   * Hands the map to the agent. The run is a queued job, so this returns
   * immediately and the page polls until the plan lands - the agent strip
   * above shows the same job while it works.
   */
  /**
   * Asks before redrawing the map.
   *
   * It used to run on the click, with one patch per rep and the pipeline
   * weighting, because those are the right defaults - but they were also the
   * only answers, and the shape of a territory map is the thing a sales
   * manager argues about most. The dialog carries the same defaults, so the
   * quick path is Enter.
   */
  planWithAgent(): void {
    if (this.planning) return;
    this.dialog
      .open(TerritoryPlanDialogComponent, {
        data: {
          reps: this.reps.length,
          manual: this.rows.filter(r => r.territory.source !== 'AgentPlan').length,
          planned: this.rows.filter(r => r.territory.source === 'AgentPlan').length,
        } satisfies TerritoryPlanFacts,
      })
      .afterClosed()
      .subscribe((choice: TerritoryPlanChoice | undefined) => {
        if (choice) this.runPlan(choice);
      });
  }

  private runPlan(choice: TerritoryPlanChoice): void {
    this.planning = true;
    this.saveError = null;
    this.planNote = 'Drawing the map…';
    this.http.post<{ jobId: string; alreadyRunning: boolean }>(
      '/api/territories/plan', choice).subscribe({
      next: res => {
        if (res.alreadyRunning) this.planNote = 'A plan is already running…';
        this.pollPlan(0);
      },
      error: err => {
        this.planning = false;
        this.planNote = null;
        this.saveError = err.status === 403
          ? 'You do not have permission to run the planner.'
          : 'Could not start the planner.';
      },
    });
  }

  /**
   * Reloads until the territory count changes or we give up. Polling rather
   * than a job-status subscription because the page already knows what
   * "finished" looks like: a different map.
   *
   * The wait is an rxjs timer rather than a bare setTimeout so that the loop
   * can be torn down with everything else this page owns. A hand-rolled
   * setTimeout sat outside the teardown chain completely: leaving the page
   * while a plan was running left the loop going, and it carried on
   * requesting, writing rows onto a destroyed component and pushing them into
   * the shared map layer - which outlives this page, so those rows would have
   * been drawn over whatever the next page had put there.
   */
  private pollPlan(attempt: number): void {
    const before = this.rows.length;
    const beforeIds = this.rows.map(r => r.territory.id).join(',');
    timer(TerritoriesComponent.PlanPollMs).pipe(
      switchMap(() => this.http.get<TerritoryWithMetrics[]>('/api/territories/metrics')),
      // Last in the pipe deliberately. It has to cancel the in-flight request
      // as well as the wait before it, and anything placed after takeUntil
      // holds its own subscription that destroy$ would no longer reach.
      takeUntil(this.destroy$),
    ).subscribe({
      next: rows => {
        const changed = rows.length !== before
          || rows.map(r => r.territory.id).join(',') !== beforeIds;
        if (changed) {
          this.rows = rows;
    this.restoreTerritoryView();
          this.feature.setRows(rows);
          this.planning = false;
          const planned = rows.filter(r => r.territory.source === 'AgentPlan').length;
          this.planNote = `The agent drew ${planned} territor${planned === 1 ? 'y' : 'ies'}.`;
          return;
        }
        if (attempt >= TerritoriesComponent.PlanPollAttempts) {
          this.planning = false;
          this.planNote = 'The planner is still running; refresh in a moment.';
          return;
        }
        this.pollPlan(attempt + 1);
      },
      error: () => {
        this.planning = false;
        this.planNote = null;
        this.saveError = 'Lost track of the planner; refresh to see the result.';
      },
    });
  }

  statusColor(status: string): string {
    if (status === 'Active') return 'var(--br-color-success)';
    if (status === 'Retired') return 'var(--br-color-muted)';
    return 'var(--br-color-warning)';
  }
}
